import { ChangeEvent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import SweetAlertService from "../../../services/SweetAlertService";
import EmployerService from "../Services/EmployerService";
import { AppConstants, employerListUrl, errorMessages, getRequiredErrorMessageForCountry, namePattern } from "../Shared/Constants/AppConstant";
import './Employer.css';

interface countryDetails {
    countryId: any;
    value: any;
    text: any;
    normalSessionLength: any;
}
export const AddEmployer = () => {
    const navigate = useNavigate();
    const { register, handleSubmit, setValue, getValues, reset, formState: { errors } }
        = useForm({ defaultValues: { hoursPerWeek: 0, minutesPerWeek: 0, sessionsPerWeek: 0, countryId: 0, employerName: "" } });

    const [countries, setCountries] = useState<countryDetails[]>([]);
    const [employmentType, setEmploymentType] = useState([]);
    const [inputValue, setInputValue] = useState("0");
    const [inputRoundedValue, setInputRoundedValue] = useState<any>();

    useEffect(() => {
        LoadEmploymentDetails();
    }, []);

    //To populate employer details
    const LoadEmploymentDetails = () => {
        EmployerService.loadEmployer().then((response) => {
            if (response.data) {
                setCountries(response.data.countries);
                setEmploymentType(response.data.employmentType);
                setValue("employerName", "Employment " + (Number(response.data.employerNames.length) + 1));
            }
        })
            .catch(() => {
                SweetAlertService.error("Error", "Something went wrong while getting Employer details");
            });
    }

    //For input field allowing only two digits 
    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const enteredValue = event.target.value;
        const sanitizedValue = enteredValue.slice(0, 2); // Extract the first two characters
        setInputValue(sanitizedValue);
    };

    //To allow only two decimal
    const allowTwoDecimals = (event: ChangeEvent<HTMLInputElement>) => {
        const enteredValue = event.target.value;
        const sanitizedValue = enteredValue.replace(/[^0-9.]/g, '');
        const decimalParts = sanitizedValue.split('.');
        const integerPart = decimalParts[0];
        const decimalPart = decimalParts[1] ? decimalParts[1].slice(0, 2) : '';
        const roundedValue = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
        setInputRoundedValue(roundedValue);
    }

    //To calculate session and hours, mins
    const CalculateSession = (e: any) => {
        var selectedCountry = Number(getValues("countryId"));
        let getCountryDetails = countries.filter(x => x.countryId == selectedCountry)[0];
        if (getCountryDetails && selectedCountry > 0) {
            var sessionLength = (getCountryDetails.normalSessionLength) ?? AppConstants.Default_Session_Length;

            let inputHrsPerWeek = getValues("hoursPerWeek");
            let inputMinsPerWeek = getValues("minutesPerWeek");
            let inputSessionPerWeek = getValues("sessionsPerWeek");
            const [hh, mm] = sessionLength.split(':');

            //Calculation if input value greater than zero
            if (Number(inputHrsPerWeek) >= 0 && Number(inputMinsPerWeek) >= 0 && Number(inputSessionPerWeek) >= 0) {
                var totalminutes = Number(hh) * 60 + Number(mm);
                var totalminutestoset = Number(inputHrsPerWeek) * 60 + Number(inputMinsPerWeek);
                if (e.name == "sessionsPerWeek") {
                    //set Hours and Minutes
                    var minutestoset = Number(inputSessionPerWeek) * totalminutes;
                    const hours = Math.floor(minutestoset / 60);
                    const minutes = Math.floor(minutestoset % 60);
                    setValue("hoursPerWeek", hours);
                    setValue("minutesPerWeek", minutes);
                }
                else if (e.name == "countryId") {
                     if (inputSessionPerWeek > 0) {
                        //set Hours and Minutes
                        var minutestoset = Number(inputSessionPerWeek) * totalminutes;
                        const hours = Math.floor(minutestoset / 60);
                        const minutes = Math.floor(minutestoset % 60);
                        setValue("hoursPerWeek", hours);
                        setValue("minutesPerWeek", minutes);
                     }
                     else {
                        //set Session
                        var session = (totalminutestoset / totalminutes).toFixed(2);
                        setValue("sessionsPerWeek", Number(session))
                     }
                }
                else {
                    //set Session
                    var session = (totalminutestoset / totalminutes).toFixed(2);
                    setValue("sessionsPerWeek", Number(session))
                }
            }
        }
    }

    //To check whether hours, minutes and sessions are with in specified range
    const isValid = (data: any) => {
        let errorMessage = "";
        let isValid = true;
        if (data.hoursPerWeek > AppConstants.Max_Hours_Per_Week || data.hoursPerWeek < 0) {
            isValid = false;
            errorMessage += errorMessages.hoursPatternRange + "<br/>";
        }
        if (data.minutesPerWeek > AppConstants.Max_Minutes_Per_Week || data.minutesPerWeek < 0) {
            isValid = false;
            errorMessage += errorMessages.minutesPatternRange + "<br/>";
        }
        if (data.sessionsPerWeek > AppConstants.Max_Session_Per_Week || data.sessionsPerWeek < 0) {
            isValid = false;
            errorMessage += errorMessages.sessionPatternRange + "<br/>";
        }
        if (!isValid) {
            SweetAlertService.errorWithHTML(AppConstants.SweetAlert_Error_Title, errorMessage);
        }
        return isValid;
    }

    const onSubmit = (data: any) => {
        data.employerName = data.employerName.substr(0, 20);
        data.countryId = Number(data.countryId);
        data.sessionsPerWeek = Number(data.sessionsPerWeek) ?? 0.0;
        data.minutesPerWeek = Number(data.minutesPerWeek) ?? 0;
        data.hoursPerWeek = Number(data.hoursPerWeek) ?? 0;
        if (isValid(data)) {
            EmployerService.addEmployer(data).then((response) => {
                if (response.data > 0) {
                    reset();
                    navigate(employerListUrl);
                    window.location.reload();
                } else {
                    SweetAlertService.error("Error", "Something went wrong while adding employer");
                }
            }).catch(error => {
                if (error.response.data.errors.EmployerName) {
                    SweetAlertService.error("Error", error.response.data.errors.EmployerName[0])
                }
                else {
                    SweetAlertService.error("Error", "Error while saving employer")
                }
            })
        }
    };

    return (
        <>
            <main>
                <div className="hero-banner hero-banner--background hero-banner--subpage">
                    <div className="container">
                        <div className="hero-banner__image-container">
                            <div className="my-4">
                                <h1>Add employment details</h1>
                            </div>
                            <div className="card-body">
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <div className="form-group">
                                        <div className="front-start">
                                            <p className="fw-bold">How do you want to refer your employment details in the diary?</p>
                                            <p> You can use an abbreviation of your employer/practice name, a date or details of your role. i.e. NLHC 2022.<br />
                                                This will be used throughout the diary, so try to make this as short as possible.
                                            </p>
                                            <div className="front-start col-sm-3 ps-0">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    {...register("employerName", {
                                                        pattern: {
                                                            value: namePattern,
                                                            message: errorMessages.pattern
                                                        }
                                                    })}
                                                />
                                                {errors.employerName && <span className="help-block m-b-none text-danger field-validation-valid">{errors.employerName.message?.toString()}</span>}
                                            </div>
                                            <br />
                                            <div className="form-group ">
                                                <label className="front-start mb-2"><b>Country</b>
                                                    <b className="text-danger ">*</b>
                                                </label>
                                                <div className="front-start col-sm-3 mb-4 ps-0">
                                                    <select className="form-select"
                                                        {...register("countryId", {
                                                            required: getRequiredErrorMessageForCountry("country"),
                                                            onChange: (e) => CalculateSession(e.target),
                                                        })}>
                                                        <option value="">Select</option>
                                                        if (countries.length)
                                                        {
                                                            countries.map((e: any) => (
                                                                <option value={e.value} key={e.value}>{e.text}</option>
                                                            ))
                                                        }
                                                    </select>
                                                    {errors.countryId && <span className="help-block m-b-none text-danger field-validation-valid">{errors.countryId.message?.toString()}</span>}
                                                </div>
                                                <p>Current BMA standards for a session of GP care is 4 hours 10 minutes. Enter your contracted hours as time/week if your contracted sessions do not reflect this.</p>
                                                <p>You may choose to leave this section blank if your role doesn't involve contracted hours.</p>
                                            </div>
                                            <div className="table-responsive-xl front-start">
                                                <table className="table customTable mb-4">
                                                    <thead>
                                                        <tr className="d-flex flex-row">
                                                            <th className="col-sm-7 customHeader"></th>
                                                            <th className="col-sm-3 customHeader">Time/week</th>
                                                            <th className="col-sm-2 customHeader">Sessions/week</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr className="d-flex flex-row ">
                                                            <td className="col-sm-7 py-4">{employmentType}</td>
                                                            <td className="d-flex flex-row col-sm-3">
                                                                <input type="number" className="form-control col-xs-3"
                                                                    {...register("hoursPerWeek", {
                                                                        onBlur: (e) => CalculateSession(e.target),
                                                                    })}
                                                                />
                                                                <label className="col-sm-1 mt-2">&nbsp;hrs </label>
                                                                <input type="number" className="form-control col-xs-3 ms-4" value={inputValue}
                                                                    onInput={handleInputChange}
                                                                    {...register("minutesPerWeek", {
                                                                        onBlur: (e) => CalculateSession(e.target),
                                                                    })}
                                                                />
                                                                <label className="col-sm-1 mt-2">&nbsp;mins</label>
                                                            </td>
                                                            <td className="d-flex flex-row col-sm-2">
                                                                <input type="number" className="form-control col-xs-6 ms-3" step={0.01} value={inputRoundedValue}
                                                                    onInput={allowTwoDecimals}
                                                                    {...register("sessionsPerWeek", {
                                                                        onBlur: (e) => CalculateSession(e.target),
                                                                    })}
                                                                />
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div className="front-start">
                                                <button className="btn btn-primary" type="submit">Submit</button>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    )
}
